import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import AnnotationsPanel from './AnnotationPanel';
const getVitalDisplayName = (vitalType) => {
    const names = {
      'heart_rate': 'Heart Rate',
      'systolic_bp': 'Systolic Blood Pressure', 
      'diastolic_bp': 'Diastolic Blood Pressure',
      'oxygen_saturation': 'Oxygen Saturation',
      'temperature': 'Body Temperature',
      'sleep_duration': 'Sleep Duration',
      'sleep_efficiency': 'Sleep Efficiency',
      'sleep_hr_lowest': 'Sleep HR (Lowest)',
      'sleep_hr_average': 'Sleep HR (Average)',
      'sleep_hrv_average': 'Sleep HRV (Average)'
    };
    return names[vitalType] || vitalType;
  };

  const getVitalUnit = (vitalType) => {
    const units = {
      'heart_rate': 'bpm',
      'systolic_bp': 'mmHg',
      'diastolic_bp': 'mmHg', 
      'oxygen_saturation': '%',
      'temperature': '°F',
      'sleep_duration': 'hours',
      'sleep_efficiency': '%',
      'sleep_hr_lowest': 'bpm',
      'sleep_hr_average': 'bpm',
      'sleep_hrv_average': 'ms'
    };
    return units[vitalType] || '';
  };

const VitalChartsComponent = ({ vitalData, baselines, annotations, eventTime }) => {
    const [vitalTypesShown, setVitalTypesShown] = React.useState({
        heart_rate: true,
      systolic_bp: true,
      diastolic_bp: true,
      oxygen_saturation: true,
      sleep_duration: true,
      sleep_efficiency: true,
      sleep_hr_lowest: true,
      sleep_hr_average: true,
      sleep_hrv_average: true,
      temperature: true,
    });
    const [showAnnotations, setShowAnnotations] = React.useState(true);
  // Transform the data for ApexCharts
  const chartConfigs = useMemo(() => {
    if (!vitalData || !baselines) return [];

    return Object.keys(vitalData).map(vitalType => {
      const readings = vitalData[vitalType] || [];
      const baseline = baselines[vitalType];
      const isSleepData = vitalType.startsWith('sleep_');

      // Sort readings by minutesFromEvent
      const sortedReadings = readings.sort((a, b) => a.minutesFromEvent - b.minutesFromEvent);

      // Create series data using minutesFromEvent as X-axis
      const series = [
        {
          name: getVitalDisplayName(vitalType),
          data: sortedReadings.map(reading => ({
            x: reading.minutesFromEvent,
            y: reading.value
          }))
        }
      ];

      // Add baseline percentile lines if available
      if (baseline && baseline.percentile25 && baseline.percentile75 && sortedReadings.length > 0) {
        const timeRange = {
          start: Math.min(...sortedReadings.map(r => r.minutesFromEvent)),
          end: Math.max(...sortedReadings.map(r => r.minutesFromEvent))
        };

        series.push(
          {
            name: '25th Percentile',
            data: [
              { x: timeRange.start, y: baseline.percentile25 },
              { x: timeRange.end, y: baseline.percentile25 }
            ]
          },
          {
            name: '75th Percentile', 
            data: [
              { x: timeRange.start, y: baseline.percentile75 },
              { x: timeRange.end, y: baseline.percentile75 }
            ]
          }
        );
      }

      // Chart options
      const options = {
        chart: {
          type: isSleepData ? 'scatter' : 'line', // Scatter for sleep (daily), line for continuous
          height: 300,
          toolbar: {
            show: false
          },
          zoom: {
            enabled: true,
            type: 'x'
          },
          animations: {
            enabled: false // Disable animations for better performance
          }
        },
        stroke: {
          curve: isSleepData ? 'straight' : 'smooth',
          width: [3, 2, 2] // Different widths for main line vs baseline lines
        },
        markers: {
          size: isSleepData ? [8, 0, 0] : [2, 0, 0], // Larger markers for sleep, smaller for continuous
          strokeWidth: isSleepData ? 2 : 1,
          fillOpacity: 1
        },
        colors: isSleepData ? ['#8B5CF6', '#FEB019', '#FF4560'] : ['#008FFB', '#FEB019', '#FF4560'], // Purple for sleep, blue for continuous
        dataLabels: {
          enabled: false // Disable data labels for performance
        },
        xaxis: {
          type: 'numeric',
          labels: {
            formatter: (value) => {
              if (isSleepData) {
                // For sleep data, show days
                const days = value / 1440; // 1440 minutes in a day
                if (Math.abs(days) >= 1) {
                  const sign = days < 0 ? '' : '+';
                  return `${sign}${days.toFixed(0)}d`;
                } else {
                  const hours = value / 60;
                  const sign = hours < 0 ? '' : '+';
                  return `${sign}${hours.toFixed(0)}h`;
                }
              } else {
                // For continuous data, show hours and minutes
                const hours = Math.abs(value) / 60;
                const sign = value < 0 ? '' : '+';
                if (hours < 1) {
                  return `${sign}${Math.abs(value)}m`;
                } else if (hours < 24) {
                  return `${sign}${hours.toFixed(1)}h`;
                } else {
                  return `${sign}${(hours / 24).toFixed(1)}d`;
                }
              }
            }
          },
          title: {
            text: 'Time from Event'
          },
          tickAmount: isSleepData ? 6 : 8
        },
        yaxis: {
          title: {
            text: getVitalUnit(vitalType)
          },
          labels: {
            formatter: (value) => value?.toFixed(1)
          }
        },
        title: {
          text: getVitalDisplayName(vitalType),
          align: 'left',
          style: {
            fontSize: '16px',
            fontWeight: 'bold'
          }
        },
        legend: {
          show: true,
          position: 'top',
          horizontalAlign: 'right'
        },
        grid: {
          show: true,
          borderColor: '#e0e0e0'
        },
        tooltip: {
          shared: !isSleepData, // Don't share tooltip for scatter plots
          intersect: isSleepData,
          x: {
            formatter: (value) => {
              if (isSleepData) {
                const days = value / 1440;
                if (Math.abs(days) >= 1) {
                  const sign = days < 0 ? '' : '+';
                  return `${sign}${days.toFixed(0)} days from event`;
                } else {
                  const hours = value / 60;
                  const sign = hours < 0 ? '' : '+';
                  return `${sign}${hours.toFixed(0)} hours from event`;
                }
              } else {
                const hours = Math.abs(value) / 60;
                const sign = value < 0 ? '' : '+';
                if (hours < 1) {
                  return `${sign}${value} minutes from event`;
                } else if (hours < 24) {
                  return `${sign}${hours.toFixed(1)} hours from event`;
                } else {
                  return `${sign}${(hours / 24).toFixed(1)} days from event`;
                }
              }
            }
          },
          y: {
            formatter: (value, { seriesIndex }) => {
              const unit = getVitalUnit(vitalType);
              return `${value?.toFixed(1)} ${unit}`;
            }
          }
        },
        annotations: {
          xaxis: [
            {
              x: 0, // Event occurs at x = 0
              borderColor: '#FF4560',
              borderWidth: 2,
              label: {
                text: 'Event',
                style: {
                  color: '#fff',
                  background: '#FF4560'
                }
              }
            }
          ]
        }
      };

      return {
        vitalType,
        series,
        options,
        isSleepData
      };
    });
  }, [vitalData, baselines, eventTime]);


  if (!chartConfigs.length) {
    return (
      <div className="flex items-center justify-center mt-4 ml-2 p-4 h-64 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <div className="text-center">
          <div className="text-gray-400 text-lg mb-2">📊</div>
          <div className="text-gray-600 font-medium">No vital data available</div>
          <div className="text-gray-500 text-sm">Check your data source and try again</div>
        </div>
      </div>
    );
  }
  

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
    <div className="flex flex-col lg:flex-row gap-6">
        <div className={`${showAnnotations ? 'lg:w-2/3' : 'w-full'} transition-all duration-300`}>
          <div className="space-y-6 md:space-y-8">
        {chartConfigs.map(({ vitalType, series, options, isSleepData }) => (
          <div key={vitalType} className="bg-white rounded-lg shadow-md border border-gray-200 p-4 md:p-6">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className='flex items-center gap-2 mb-2'>
              <div className={`w-3 h-3 rounded-full ${isSleepData ? 'bg-purple-500' : 'bg-blue-500'}`}></div>
              <span className="text-sm text-gray-600">
                {isSleepData ? `Daily Sleep Data (${getVitalDisplayName(vitalType)})` : `Continuous Monitoring (${getVitalDisplayName(vitalType)})`}
              </span>
              </div>
               <button className="bg-black text-white rounded-md pl-4 pr-4 pt-2 pb-2" onClick={() => 
                setVitalTypesShown((prev) => ({
                    ...prev,
                    [vitalType]: !prev[vitalType]
                }))
            }>{vitalTypesShown[vitalType] ? "Hide" : "Show"}</button>
            </div>
            {vitalTypesShown[vitalType] && <><Chart
              options={options}
              series={series}
              type={isSleepData ? 'scatter' : 'line'}
              height={300}
            />
            {baselines[vitalType] && (
              <div className="text-xs text-gray-600 mt-3 text-center bg-gray-50 p-2 rounded">
                <span className="font-medium">Baseline:</span> 25th percentile = {baselines[vitalType].percentile25?.toFixed(1)}, 
                75th percentile = {baselines[vitalType].percentile75?.toFixed(1)}
                {baselines[vitalType].dataPoints && (
                  <span className="text-gray-500"> ({baselines[vitalType].dataPoints} data points)</span>
                )}
              </div>
            )}</>}
          </div>
        ))}
        </div>
        </div>
        <AnnotationsPanel 
          annotations={annotations}
          isVisible={showAnnotations}
          onToggle={() => setShowAnnotations(!showAnnotations)}
        />
      </div>
    </div>
  );
};

export default VitalChartsComponent;